import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { renewalService, getErrorMessage } from '@/services';
// Imported from the module rather than `@/services` so this needs no edit to
// the shared services barrel, which several agents touch.
import type { ServicingRequestStatusUpdate } from '@/services/renewals';
import type {
  RenewalBatchFilters,
  ServicingRequestFilters,
  RenewalBatch,
  ServicingRequest,
} from '@/types';
import type {
  RenewalPaymentConfirmPayload,
  RenewalPaymentLinkCreatePayload,
  RenewalQueueActivityPayload,
  RenewalQueueFilters,
  RenewalQueueUpdateStatusPayload,
} from '@/types/renewalQueue';

// B9: the policy-based renewal queue
// (docs/superpowers/specs/2026-09-15-b9-plan.md §2). There is no
// "fetch pending" mutation here -- queue membership comes straight from
// policy data, so there is nothing left to materialise on demand. The old
// `useFetchPendingRenewals` hook, its button and its service call are gone.
export function useRenewalQueue(page: number = 1, filters?: RenewalQueueFilters) {
  return useQuery({
    queryKey: ['renewal-queue', page, filters],
    queryFn: () => renewalService.getRenewalQueue(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

/** `id` may be a policy id or a legacy Renewal id. */
export function useRenewalQueueItem(id: string) {
  return useQuery({
    queryKey: ['renewal-queue-item', id],
    queryFn: () => renewalService.getRenewalQueueItem(id),
    enabled: !!id,
  });
}

// `assign`/`update_status`/`log_activity` all respond with `item`: the
// freshly re-fetched queue detail row for this exact policy.
//
// The detail page is keyed on its ROUTE id, which may be a legacy Renewal id
// (old notification links) rather than the policy id the POST goes to. So the
// response is written under both ids, and the whole `['renewal-queue-item']`
// prefix is invalidated too -- any other cached id for the same policy, or a
// caller that did not pass `routeId`, still refetches instead of silently
// showing the pre-update row after a success toast. The list, the summary
// badges (which apply the status filter) and the dashboard widget are
// invalidated as well; none of them can be patched from one row.
type QueueMutationIds = { policyId: string; routeId?: string };

function syncQueueCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  { policyId, routeId }: QueueMutationIds,
  item: unknown
) {
  const written = new Set([policyId, routeId].filter(Boolean) as string[]);
  written.forEach((id) => queryClient.setQueryData(['renewal-queue-item', id], item));
  queryClient.invalidateQueries({
    queryKey: ['renewal-queue-item'],
    // Rows just written from the response are already current.
    predicate: (query) => !written.has(String(query.queryKey[1])),
  });
  queryClient.invalidateQueries({ queryKey: ['renewal-queue'] });
  queryClient.invalidateQueries({ queryKey: ['upcomingRenewals'] });
}

export function useAssignRenewalQueueItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ policyId, assignedTo }: QueueMutationIds & { assignedTo: string }) =>
      renewalService.assignRenewalQueueItem(policyId, assignedTo),
    onSuccess: (data, variables) => {
      syncQueueCaches(queryClient, variables, data.item);
      toast.success(data.message || 'Renewal assigned successfully');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useUpdateRenewalQueueItemStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      policyId,
      routeId: _routeId,
      ...payload
    }: QueueMutationIds & RenewalQueueUpdateStatusPayload) =>
      renewalService.updateRenewalQueueItemStatus(policyId, payload),
    onSuccess: (data, variables) => {
      syncQueueCaches(queryClient, variables, data.item);
      toast.success(data.message || 'Renewal status updated');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

// #345: the insurer's renewal payment link (desk side). Each write answers with
// `item`, the re-fetched queue detail, so the page updates from the response.
export function useRenewalPaymentLinks(policyId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['renewal-payment-links', policyId],
    queryFn: () => renewalService.listPaymentLinks(policyId!),
    enabled: !!policyId && enabled,
  });
}

function syncPaymentLinkCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  ids: QueueMutationIds,
  item: unknown
) {
  if (item) syncQueueCaches(queryClient, ids, item);
  queryClient.invalidateQueries({ queryKey: ['renewal-payment-links', ids.policyId] });
  queryClient.invalidateQueries({ queryKey: ['policyPayments'] });
}

export function useCreateRenewalPaymentLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ policyId, routeId: _routeId, ...payload }: QueueMutationIds & RenewalPaymentLinkCreatePayload) =>
      renewalService.createPaymentLink(policyId, payload),
    onSuccess: (data, variables) => {
      syncPaymentLinkCaches(queryClient, variables, data.item);
      toast.success('Payment link saved');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useRevokeRenewalPaymentLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, reason }: QueueMutationIds & { linkId: string; reason?: string }) =>
      renewalService.revokePaymentLink(linkId, reason),
    onSuccess: (data, variables) => {
      syncPaymentLinkCaches(queryClient, variables, data.item);
      toast.success('Payment link revoked');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useRejectRenewalPaymentReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, reason }: QueueMutationIds & { linkId: string; reason?: string }) =>
      renewalService.rejectPaymentReport(linkId, reason),
    onSuccess: (data, variables) => {
      syncPaymentLinkCaches(queryClient, variables, data.item);
      toast.success('Customer told the payment was not found');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useConfirmRenewalPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      linkId,
      policyId: _policyId,
      routeId: _routeId,
      ...payload
    }: QueueMutationIds & { linkId: string } & RenewalPaymentConfirmPayload) =>
      renewalService.confirmRenewalPayment(linkId, payload),
    onSuccess: (data, variables) => {
      syncPaymentLinkCaches(queryClient, variables, data.item);
      toast.success('Payment confirmed; the customer has their receipt');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

export function useLogRenewalQueueItemActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      policyId,
      routeId: _routeId,
      ...payload
    }: QueueMutationIds & RenewalQueueActivityPayload) =>
      renewalService.logRenewalQueueItemActivity(policyId, payload),
    onSuccess: (data, variables) => {
      syncQueueCaches(queryClient, variables, data.item);
      toast.success(data.message || 'Activity logged');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

// B9 removed `GET /renewals/summary/` with no replacement on this table --
// see the comment on `renewalService` in `services/renewals.ts`. Its
// `useRenewalSummary` hook is deleted, and so is its successor
// `useRenewalQueueSummary` (Cust-4/U-03: the tab counts it fed were removed).
//
// `useRenewals`, `useRenewal`, `useUpdateRenewal`, `useAssignRenewal` and
// `useUpdateRenewalStatus` (the legacy id-keyed list/detail/write hooks) had
// no caller left once the policy-keyed queue hooks above took over -- and
// `useUpdateRenewal`'s PATCH now 405s since B9 made `RenewalViewSet`
// read-only. Removed rather than kept dead, along with the service methods
// only they used.

// Batches
export function useRenewalBatches(page: number = 1, filters?: RenewalBatchFilters) {
  return useQuery({
    queryKey: ['renewal-batches', page, filters],
    queryFn: () => renewalService.getBatches(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useRenewalBatch(id: string) {
  return useQuery({
    queryKey: ['renewal-batch', id],
    queryFn: () => renewalService.getBatch(id),
    enabled: !!id,
  });
}

export function useCreateRenewalBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<RenewalBatch>) => renewalService.createBatch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['renewal-batches'] });
      toast.success('Batch created successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateRenewalBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RenewalBatch> }) =>
      renewalService.updateBatch(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['renewal-batches'] });
      queryClient.invalidateQueries({ queryKey: ['renewal-batch', variables.id] });
      toast.success('Batch updated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

// Servicing Requests
export function useServicingRequests(page: number = 1, filters?: ServicingRequestFilters) {
  return useQuery({
    queryKey: ['servicing-requests', page, filters],
    queryFn: () => renewalService.getServicingRequests(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useServicingRequest(id: string) {
  return useQuery({
    queryKey: ['servicing-request', id],
    queryFn: () => renewalService.getServicingRequest(id),
    enabled: !!id,
    retry: false,
  });
}

/** B2: move a servicing request along. The backend decides who may; this only
 *  hides the control from audiences that would get a 403 anyway. */
export function useUpdateServicingRequestStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & ServicingRequestStatusUpdate) =>
      renewalService.updateServicingRequestStatus(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['servicing-requests'] });
      queryClient.invalidateQueries({ queryKey: ['servicing-request', variables.id] });
      toast.success('Request status updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/** #375: reopen a completed / rejected / cancelled request (Broker Admin). */
export function useReopenServicingRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      renewalService.reopenServicingRequest(id, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['servicing-requests'] });
      queryClient.invalidateQueries({ queryKey: ['servicing-request', variables.id] });
      toast.success('Request reopened');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useCreateServicingRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<ServicingRequest>) => renewalService.createServicingRequest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['servicing-requests'] });
      toast.success('Request created successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
